import { useEffect, useState, useRef } from 'react';
import { motion, useScroll, useTransform, AnimatePresence } from 'framer-motion';
import { Menu, X, Instagram, Mail, ChevronDown, Star, ArrowRight, Upload, Check } from 'lucide-react';
import { getPortfolio, getTestimonials, getSettings, createBooking, createPaymentSession, verifyPayment, submitReview, seedData } from '../lib/api';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { format } from 'date-fns';

const ARTIST_IMAGE = "/logo.png";
const ABOUT_IMAGE = "https://customer-assets.emergentagent.com/job_dark-artisan-1/artifacts/16jd8ymb_WhatsApp%20Image%202026-02-23%20at%2015.44.08.jpeg";

// Navigation Component
const Navigation = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollTo = (id) => {
    setIsOpen(false);
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <motion.nav
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${scrolled ? 'glass py-4' : 'py-6'}`}
    >
      <div className="max-w-[1400px] mx-auto px-6 flex items-center justify-between">
        <motion.a
          href="/"
          className="font-heading text-2xl tracking-tight"
          whileHover={{ scale: 1.05 }}
          data-testid="nav-logo"
        >
          TATTOOS BY HET
        </motion.a>

        {/* Desktop Nav */}
        <div className="hidden md:flex items-center gap-8">
          {['work', 'about', 'styles', 'testimonials', 'policies', 'booking'].map((item) => (
            <button
              key={item}
              onClick={() => scrollTo(item)}
              className="text-sm uppercase tracking-widest text-slate-400 hover:text-white transition-colors"
              data-testid={`nav-${item}`}
            >
              {item}
            </button>
          ))}
          <a href="/admin" className="text-sm uppercase tracking-widest text-slate-400 hover:text-white transition-colors" data-testid="nav-admin">
            Admin
          </a>
        </div>

        {/* Mobile Menu Button */}
        <button
          className="md:hidden text-white"
          onClick={() => setIsOpen(!isOpen)}
          data-testid="mobile-menu-btn"
        >
          {isOpen ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      {/* Mobile Nav */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="md:hidden glass mt-4"
          >
            <div className="flex flex-col items-center py-8 gap-6">
              {['work', 'about', 'styles', 'testimonials', 'policies', 'booking'].map((item) => (
                <button
                  key={item}
                  onClick={() => scrollTo(item)}
                  className="text-lg uppercase tracking-widest text-slate-400 hover:text-white transition-colors"
                >
                  {item}
                </button>
              ))}
              <a href="/admin" className="text-lg uppercase tracking-widest text-slate-400 hover:text-white transition-colors">
                Admin
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.nav>
  );
};

// Hero Section - Full Screen Cinematic
const HeroSection = () => {
  const containerRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ["start start", "end start"]
  });

  const y = useTransform(scrollYProgress, [0, 1], [0, 200]);
  const opacity = useTransform(scrollYProgress, [0, 0.5], [1, 0]);
  const scale = useTransform(scrollYProgress, [0, 0.5], [1, 1.1]);
  const textY = useTransform(scrollYProgress, [0, 1], [0, 100]);

  return (
    <section
      ref={containerRef}
      className="relative h-screen overflow-hidden bg-dark"
      data-testid="hero-section"
    >
      {/* Full Screen Background Image */}
      <motion.div
        style={{ scale }}
        className="absolute inset-0 z-0 flex items-center justify-center"
      >
        <img
          src={ARTIST_IMAGE}
          alt="Het - Tattoo Artist"
          className="h-[70vh] object-contain opacity-90"
          data-testid="hero-artist-image"
        />
        {/* Dark overlay for better text readability */}
        <div className="absolute inset-0 bg-gradient-to-t from-dark via-dark/40 to-dark/20" />
        <div className="absolute inset-0 bg-gradient-to-r from-dark/30 via-transparent to-dark/30" />
      </motion.div>

      {/* Purple/Crimson Glow Effect */}
      <div className="absolute inset-0 z-5 pointer-events-none">
        <motion.div
          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[120%] h-[120%] opacity-40"
          style={{
            background: 'radial-gradient(ellipse at center, rgba(124, 58, 237, 0.3) 0%, rgba(190, 18, 60, 0.15) 30%, transparent 60%)',
          }}
          animate={{
            scale: [1, 1.05, 1],
            opacity: [0.4, 0.5, 0.4],
          }}
          transition={{
            duration: 6,
            repeat: Infinity,
            ease: "easeInOut"
          }}
        />
      </div>


      {/* Foreground Content */}
      <motion.div
        style={{ opacity }}
        className="absolute inset-0 flex flex-col items-center justify-end pb-24 z-20"
      >
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.8, duration: 0.6 }}
          className="text-xs uppercase tracking-[0.3em] text-slate-300 mb-4"
        >
          Luxury Custom Tattoo Studio
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1, duration: 0.6 }}
          className="flex gap-4"
        >
          <button
            onClick={() => document.getElementById('work')?.scrollIntoView({ behavior: 'smooth' })}
            className="btn-cinematic bg-white text-black px-8 py-4 rounded-full text-sm font-bold tracking-widest uppercase hover:bg-slate-200 transition-all hover:scale-105"
            data-testid="hero-view-work-btn"
          >
            View Work
          </button>
          <button
            onClick={() => document.getElementById('booking')?.scrollIntoView({ behavior: 'smooth' })}
            className="btn-cinematic border border-white/30 text-white px-8 py-4 rounded-full text-sm font-bold tracking-widest uppercase hover:border-white hover:bg-white/10 transition-all"
            data-testid="hero-book-btn"
          >
            Book Now
          </button>
        </motion.div>
      </motion.div>

      {/* Scroll Indicator */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.5 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 z-20"
      >
        <motion.div
          animate={{ y: [0, 10, 0] }}
          transition={{ duration: 2, repeat: Infinity }}
        >
          <ChevronDown className="text-white/50" size={24} />
        </motion.div>
      </motion.div>

      {/* Particles */}
      <div className="particles z-15">
        {[...Array(15)].map((_, i) => (
          <motion.div
            key={i}
            className="particle"
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
            }}
            animate={{
              y: [0, -30, 0],
              opacity: [0.3, 0.8, 0.3],
            }}
            transition={{
              duration: 3 + Math.random() * 2,
              repeat: Infinity,
              delay: Math.random() * 2,
            }}
          />
        ))}
      </div>
    </section>
  );
};

// About Section
const AboutSection = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"]
  });

  const y = useTransform(scrollYProgress, [0, 1], [100, -100]);

  return (
    <section
      id="about"
      ref={ref}
      className="py-32 bg-dark relative overflow-hidden"
      data-testid="about-section"
    >
      <div className="max-w-[1400px] mx-auto px-6">
        <div className="grid md:grid-cols-2 gap-16 items-center">
          {/* Image */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="relative"
          >
            <div className="aspect-[4/5] rounded-2xl overflow-hidden">
              <img
                src={ABOUT_IMAGE}
                alt="Het - Tattoo Artist"
                className="w-full h-full object-cover image-hover"
              />
            </div>
          </motion.div>

          {/* Content */}
          <div>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-xs uppercase tracking-[0.3em] text-accent-purple mb-4"
            >
              The Artist
            </motion.p>
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="section-title mb-8"
            >
              Creating Art<br />That Lives
            </motion.h2>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
              className="text-lg text-slate-400 font-light leading-relaxed mb-6"
            >
              With 6+ years of experience, Het has refined his craft into something beyond traditional tattooing. Each piece is a collaboration—a fusion of your vision and his artistic expertise.
            </motion.p>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.3 }}
              className="text-lg text-slate-400 font-light leading-relaxed mb-8"
            >
              Specializing in blackwork, geometric, and ornamental styles, Het creates pieces that are not just tattoos—they're statements. Permanent art that moves with you.
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.4 }}
              className="flex gap-6"
            >
              <div>
                <p className="text-4xl font-heading text-white">6+</p>
                <p className="text-sm text-slate-500 uppercase tracking-wider">Years Experience</p>
              </div>
              <div>
                <p className="text-4xl font-heading text-white">2000+</p>
                <p className="text-sm text-slate-500 uppercase tracking-wider">Pieces Created</p>
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
};

// Styles Section - Horizontal Scroll
const StylesSection = ({ portfolio }) => {
  const styles = [
    { name: "Blackwork", description: "Bold, solid black designs with striking contrast", image: "https://images.unsplash.com/photo-1623483467074-8324576d5b14?w=600" },
    { name: "Geometric", description: "Sacred patterns and mathematical precision", image: "https://images.unsplash.com/photo-1566975293313-34b1fd638bc5?w=600" },
    { name: "Ornamental", description: "Flowing designs inspired by art nouveau", image: "https://images.pexels.com/photos/14565781/pexels-photo-14565781.jpeg?w=600" },
    { name: "Realism", description: "Hyper-realistic portraits and imagery", image: "https://images.unsplash.com/photo-1611501275019-9b5cda994e8d?w=600" },
    { name: "Fine Line", description: "Delicate, intricate linework", image: "https://images.unsplash.com/photo-1562962230-16e4623d36e6?w=600" },
  ];

  return (
    <section
      id="styles"
      className="py-32 bg-dark-surface relative overflow-hidden"
      data-testid="styles-section"
    >
      <div className="max-w-[1400px] mx-auto px-6 mb-12">
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-xs uppercase tracking-[0.3em] text-accent-purple mb-4"
        >
          Signature Styles
        </motion.p>
        <motion.h2
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.1 }}
          className="section-title"
        >
          Mastered Techniques
        </motion.h2>
      </div>

      {/* Horizontal Scroll */}
      <div className="horizontal-scroll gap-6 px-6 pb-8">
        {styles.map((style, index) => (
          <motion.div
            key={style.name}
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: index * 0.1 }}
            className="w-[350px] md:w-[400px] glass rounded-2xl overflow-hidden group cursor-pointer"
            data-testid={`style-card-${style.name.toLowerCase()}`}
          >
            <div className="aspect-[4/5] overflow-hidden">
              <img
                src={style.image}
                alt={style.name}
                className="w-full h-full object-cover image-hover group-hover:scale-110 transition-transform duration-700"
              />
            </div>
            <div className="p-6">
              <h3 className="font-heading text-2xl mb-2">{style.name}</h3>
              <p className="text-slate-400 text-sm">{style.description}</p>
            </div>
            <div className="absolute inset-0 border-2 border-transparent group-hover:border-accent-purple/30 rounded-2xl transition-colors duration-500 pointer-events-none" />
          </motion.div>
        ))}
      </div>
    </section>
  );
};

// Portfolio Gallery
const GallerySection = ({ portfolio }) => {
  const [selectedWork, setSelectedWork] = useState(null);

  return (
    <section
      id="work"
      className="py-32 bg-dark relative"
      data-testid="gallery-section"
    >
      <div className="max-w-[1400px] mx-auto px-6">
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-xs uppercase tracking-[0.3em] text-accent-purple mb-4"
        >
          Portfolio
        </motion.p>
        <motion.h2
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.1 }}
          className="section-title mb-12"
        >
          Featured Work
        </motion.h2>

        {/* Uniform Grid */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 md:gap-6">
          {portfolio.map((item, index) => (
            <motion.div
              key={item.id}
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.05 }}
              className="group cursor-pointer"
              onClick={() => setSelectedWork(item)}
              data-testid={`portfolio-item-${index}`}
            >
              <div className="relative aspect-square overflow-hidden rounded-xl border border-white/5 hover:border-accent-purple/30 transition-all duration-500">
                <img
                  src={item.image_url}
                  alt={item.title}
                  className="w-full h-full object-cover transition-all duration-700 group-hover:scale-110 filter grayscale-[20%] group-hover:grayscale-0"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-dark via-dark/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                <div className="absolute bottom-0 left-0 right-0 p-4 translate-y-full group-hover:translate-y-0 transition-transform duration-500">
                  <p className="text-xs uppercase tracking-widest text-accent-purple mb-1">{item.style}</p>
                  <h3 className="font-heading text-lg">{item.title}</h3>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>

      {/* Work Detail Modal */}
      <Dialog open={!!selectedWork} onOpenChange={() => setSelectedWork(null)}>
        <DialogContent className="bg-dark-surface border-white/10 max-w-3xl">
          {selectedWork && (
            <>
              <div className="aspect-square overflow-hidden rounded-lg mb-6">
                <img
                  src={selectedWork.image_url}
                  alt={selectedWork.title}
                  className="w-full h-full object-cover"
                />
              </div>
              <DialogHeader>
                <p className="text-xs uppercase tracking-widest text-accent-purple mb-2">{selectedWork.style}</p>
                <DialogTitle className="font-heading text-3xl">{selectedWork.title}</DialogTitle>
              </DialogHeader>
              <p className="text-slate-400 mb-4">{selectedWork.description}</p>
              <div className="flex gap-8 text-sm">
                <div>
                  <p className="text-slate-500 uppercase tracking-wider text-xs mb-1">Placement</p>
                  <p className="text-white">{selectedWork.placement}</p>
                </div>
                <div>
                  <p className="text-slate-500 uppercase tracking-wider text-xs mb-1">Session Time</p>
                  <p className="text-white">{selectedWork.session_time}</p>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </section>
  );
};

// Studio Section
const StudioSection = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"]
  });

  const y1 = useTransform(scrollYProgress, [0, 1], [0, -100]);
  const y2 = useTransform(scrollYProgress, [0, 1], [0, 100]);

  return (
    <section
      ref={ref}
      className="py-32 bg-dark-surface relative overflow-hidden"
      data-testid="studio-section"
    >
      <div className="max-w-[1400px] mx-auto px-6">
        <div className="grid md:grid-cols-2 gap-16 items-center">
          <div>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-xs uppercase tracking-[0.3em] text-accent-purple mb-4"
            >
              The Studio
            </motion.p>
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="section-title mb-8"
            >
              Where Art<br />Comes Alive
            </motion.h2>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
              className="text-lg text-slate-400 font-light leading-relaxed mb-8"
            >
              Step into an atmosphere designed for creation. Every element of the studio—from the ambient lighting to the carefully curated music—is crafted to make your experience as memorable as the art itself.
            </motion.p>
            <motion.ul
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.3 }}
              className="space-y-3"
            >
              {['Private booking sessions', 'Medical-grade sterilization', 'Premium aftercare products', 'Comfortable lounge area'].map((item) => (
                <li key={item} className="flex items-center gap-3 text-slate-300">
                  <Check className="text-accent-purple" size={16} />
                  {item}
                </li>
              ))}
            </motion.ul>
          </div>

          <div className="relative">
            <motion.div
              style={{ y: y1 }}
              className="aspect-[4/5] rounded-2xl overflow-hidden"
            >
              <img
                src="https://images.pexels.com/photos/4125602/pexels-photo-4125602.jpeg?w=800"
                alt="Studio"
                className="w-full h-full object-cover"
              />
            </motion.div>
            <motion.div
              style={{ y: y2 }}
              className="absolute -bottom-12 -left-12 w-48 h-64 rounded-xl overflow-hidden border-4 border-dark-surface hidden md:block"
            >
              <img
                src="https://images.unsplash.com/photo-1589560486116-5a51b3340fe8?w=400"
                alt="Studio atmosphere"
                className="w-full h-full object-cover"
              />
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
};

// Testimonials Section
const TestimonialsSection = ({ testimonials }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [isSubmittingReview, setIsSubmittingReview] = useState(false);
  const [reviewForm, setReviewForm] = useState({
    client_name: '',
    email: '',
    rating: 5,
    content: ''
  });

  const handleReviewSubmit = async (e) => {
    e.preventDefault();
    setIsSubmittingReview(true);
    try {
      await submitReview(reviewForm);
      toast.success('Thank you for your review! It has been submitted for approval.');
      setReviewForm({ client_name: '', email: '', rating: 5, content: '' });
      setShowReviewModal(false);
    } catch (error) {
      toast.error('Failed to submit review. Please try again.');
    } finally {
      setIsSubmittingReview(false);
    }
  };

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % testimonials.length);
    }, 5000);
    return () => clearInterval(interval);
  }, [testimonials.length]);

  if (testimonials.length === 0) return null;

  return (
    <section
      id="testimonials"
      className="py-32 bg-dark relative overflow-hidden"
      data-testid="testimonials-section"
    >
      <div className="max-w-[1000px] mx-auto px-6 text-center">
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-xs uppercase tracking-[0.3em] text-accent-purple mb-4"
        >
          Testimonials
        </motion.p>
        <motion.h2
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.1 }}
          className="section-title mb-8"
        >
          Client Stories
        </motion.h2>

        <div className="mb-12">
          <button
            onClick={() => setShowReviewModal(true)}
            className="text-accent-purple border border-accent-purple/30 px-6 py-2 rounded-full text-sm hover:bg-accent-purple/10 transition-all"
            data-testid="leave-review-btn"
          >
            Leave a Review
          </button>
        </div>

        <div className="relative h-[300px]">
          <AnimatePresence mode="wait">
            <motion.div
              key={currentIndex}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.5 }}
              className="absolute inset-0"
            >
              <div className="flex justify-center mb-6">
                {[...Array(testimonials[currentIndex]?.rating || 5)].map((_, i) => (
                  <Star key={i} className="text-accent-purple fill-accent-purple" size={20} />
                ))}
              </div>
              <p className="text-2xl md:text-3xl font-light text-white leading-relaxed mb-8">
                "{testimonials[currentIndex]?.content}"
              </p>
              <p className="text-accent-purple font-medium">
                {testimonials[currentIndex]?.client_name}
              </p>
            </motion.div>
          </AnimatePresence>
        </div>

        {/* Dots */}
        <div className="flex justify-center gap-2 mt-8">
          {testimonials.map((_, index) => (
            <button
              key={index}
              onClick={() => setCurrentIndex(index)}
              className={`w-2 h-2 rounded-full transition-all ${index === currentIndex ? 'bg-accent-purple w-8' : 'bg-white/20'}`}
              data-testid={`testimonial-dot-${index}`}
            />
          ))}
        </div>
      </div>

      <Dialog open={showReviewModal} onOpenChange={setShowReviewModal}>
        <DialogContent className="bg-dark-surface border-white/10">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl">Leave a Review</DialogTitle>
          </DialogHeader>
          <form className="space-y-4 py-4" onSubmit={handleReviewSubmit}>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-xs uppercase tracking-widest text-slate-500 mb-1 block">Name *</label>
                <input
                  type="text"
                  required
                  value={reviewForm.client_name}
                  onChange={(e) => setReviewForm({ ...reviewForm, client_name: e.target.value })}
                  className="input-underline"
                  placeholder="Your name"
                />
              </div>
              <div>
                <label className="text-xs uppercase tracking-widest text-slate-500 mb-1 block">Email *</label>
                <input
                  type="email"
                  required
                  value={reviewForm.email}
                  onChange={(e) => setReviewForm({ ...reviewForm, email: e.target.value })}
                  className="input-underline"
                  placeholder="your@email.com"
                />
              </div>
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest text-slate-500 mb-1 block">Rating</label>
              <div className="flex gap-2 mt-2">
                {[1, 2, 3, 4, 5].map((star) => (
                  <button
                    key={star}
                    type="button"
                    onClick={() => setReviewForm({ ...reviewForm, rating: star })}
                    className={`transition-colors ${star <= reviewForm.rating ? 'text-accent-purple' : 'text-slate-600'}`}
                  >
                    <Star size={24} fill={star <= reviewForm.rating ? 'currentColor' : 'none'} />
                  </button>
                ))}
              </div>
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest text-slate-500 mb-1 block">Review *</label>
              <textarea
                required
                value={reviewForm.content}
                onChange={(e) => setReviewForm({ ...reviewForm, content: e.target.value })}
                rows={4}
                className="input-underline resize-none"
                placeholder="Share your experience..."
              />
            </div>
            <button
              type="submit"
              disabled={isSubmittingReview}
              className="w-full bg-accent-purple text-white py-3 rounded-lg font-bold tracking-widest uppercase hover:bg-accent-purple/80 transition-all disabled:opacity-50"
            >
              {isSubmittingReview ? 'Submitting...' : 'Submit Review'}
            </button>
          </form>
        </DialogContent>
      </Dialog>
    </section>
  );
};

// Booking Section
const BookingSection = ({ settings, forceSuccess }) => {
  const [formData, setFormData] = useState({
    full_name: '',
    email: '',
    phone: '',
    tattoo_placement: '',
    budget_range: '',
    description: '',
    reference_images: [],
    preferred_date: '',
    consent: false
  });
  const [date, setDate] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const placements = ['Arm', 'Forearm', 'Upper Arm', 'Full Sleeve', 'Leg', 'Thigh', 'Calf', 'Back', 'Chest', 'Ribs', 'Neck', 'Hand', 'Other'];
  const budgets = ['£200 - £500', '£500 - £1000', '£1000 - £2000', '£2000 - £5000', '£5000+'];

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.consent) {
      toast.error('Please accept the terms to continue');
      return;
    }

    setIsSubmitting(true);
    try {
      // 1. Create the booking (status: pending_payment)
      const res = await createBooking({
        ...formData,
        preferred_date: date ? format(date, 'PPP') : ''
      });

      const bookingId = res.data.id;

      // 2. Create Stripe session
      const sessionRes = await createPaymentSession(bookingId);

      // 3. Redirect to Stripe
      window.location.href = sessionRes.data.url;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to initiate booking');
      setIsSubmitting(false);
    }
  };

  if (submitted || forceSuccess) {
    return (
      <section id="booking" className="py-32 bg-dark-surface" data-testid="booking-section">
        <div className="max-w-[600px] mx-auto px-6 text-center">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="glass rounded-2xl p-12"
          >
            <div className="w-20 h-20 rounded-full bg-accent-purple/20 flex items-center justify-center mx-auto mb-6">
              <Check className="text-accent-purple" size={40} />
            </div>
            <h2 className="font-heading text-3xl mb-4">Request Submitted</h2>
            <p className="text-slate-400">
              Thank you for your £30 deposit. Het will review your request and get back to you within 48 hours.
            </p>
          </motion.div>
        </div>
      </section>
    );
  }

  return (
    <section
      id="booking"
      className="py-32 bg-dark-surface relative"
      data-testid="booking-section"
    >
      <div className="max-w-[1000px] mx-auto px-6">
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-xs uppercase tracking-[0.3em] text-accent-purple mb-4 text-center"
        >
          Book a Session
        </motion.p>
        <motion.h2
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.1 }}
          className="section-title mb-4 text-center"
        >
          Start Your Journey
        </motion.h2>
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.2 }}
          className="text-slate-400 text-center mb-12 max-w-xl mx-auto"
        >
          {settings?.availability_message || "Currently accepting new clients. Fill out the form below and Het will personally review your request."}
        </motion.p>

        {!settings?.books_open ? (
          <div className="glass rounded-2xl p-12 text-center">
            <h3 className="font-heading text-2xl mb-4">Books Currently Closed</h3>
            <p className="text-slate-400">Please check back later or follow on Instagram for updates.</p>
          </div>
        ) : (
          <motion.form
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.3 }}
            onSubmit={handleSubmit}
            className="glass rounded-2xl p-8 md:p-12"
          >
            <div className="grid md:grid-cols-2 gap-8 mb-8">
              <div>
                <label className="text-xs uppercase tracking-widest text-slate-500 mb-2 block">Full Name *</label>
                <input
                  type="text"
                  required
                  value={formData.full_name}
                  onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                  className="input-underline"
                  placeholder="Your name"
                  data-testid="booking-name-input"
                />
              </div>
              <div>
                <label className="text-xs uppercase tracking-widest text-slate-500 mb-2 block">Email *</label>
                <input
                  type="email"
                  required
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="input-underline"
                  placeholder="your@email.com"
                  data-testid="booking-email-input"
                />
              </div>
              <div>
                <label className="text-xs uppercase tracking-widest text-slate-500 mb-2 block">Phone *</label>
                <input
                  type="tel"
                  required
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  className="input-underline"
                  placeholder="+1 (555) 000-0000"
                  data-testid="booking-phone-input"
                />
              </div>
              <div>
                <label className="text-xs uppercase tracking-widest text-slate-500 mb-2 block">Preferred Date</label>
                <Popover>
                  <PopoverTrigger asChild>
                    <button
                      type="button"
                      className="input-underline text-left"
                      data-testid="booking-date-trigger"
                    >
                      {date ? format(date, 'PPP') : 'Select a date'}
                    </button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0 bg-dark-surface border-white/10">
                    <Calendar
                      mode="single"
                      selected={date}
                      onSelect={setDate}
                      disabled={(date) => date < new Date()}
                      className="bg-dark-surface text-white"
                    />
                  </PopoverContent>
                </Popover>
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-8 mb-8">
              <div>
                <label className="text-xs uppercase tracking-widest text-slate-500 mb-2 block">Placement *</label>
                <select
                  required
                  value={formData.tattoo_placement}
                  onChange={(e) => setFormData({ ...formData, tattoo_placement: e.target.value })}
                  className="input-underline bg-transparent"
                  data-testid="booking-placement-select"
                >
                  <option value="" className="bg-dark">Select area</option>
                  {placements.map((p) => (
                    <option key={p} value={p} className="bg-dark">{p}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-xs uppercase tracking-widest text-slate-500 mb-2 block">Budget Range (£) *</label>
                <select
                  required
                  value={formData.budget_range}
                  onChange={(e) => setFormData({ ...formData, budget_range: e.target.value })}
                  className="input-underline bg-transparent"
                  data-testid="booking-budget-select"
                >
                  <option value="" className="bg-dark">Select budget</option>
                  {budgets.map((b) => (
                    <option key={b} value={b} className="bg-dark">{b}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="mb-8">
              <label className="text-xs uppercase tracking-widest text-slate-500 mb-2 block">Describe Your Vision *</label>
              <textarea
                required
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                rows={4}
                className="input-underline resize-none"
                placeholder="Tell us about your tattoo idea, style preferences, and any specific details..."
                data-testid="booking-description-textarea"
              />
            </div>

            <div className="mb-8">
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.consent}
                  onChange={(e) => setFormData({ ...formData, consent: e.target.checked })}
                  className="mt-1"
                  data-testid="booking-consent-checkbox"
                />
                <span className="text-sm text-slate-400">
                  I understand that submitting this form is a request for consultation, not a confirmed appointment. I consent to being contacted regarding my tattoo inquiry.
                </span>
              </label>
            </div>

            <button
              type="submit"
              disabled={isSubmitting}
              className="btn-cinematic bg-white text-black px-12 py-4 rounded-full text-sm font-bold tracking-widest uppercase hover:bg-slate-200 transition-all hover:scale-105 disabled:opacity-50 w-full md:w-auto"
              data-testid="booking-submit-btn"
            >
              {isSubmitting ? 'Submitting...' : 'Submit Request'}
            </button>
          </motion.form>
        )}
      </div>
    </section>
  );
};

// Policies Section
const PoliciesSection = () => {
  const policies = [
    {
      title: "Deposit Required",
      description: "A non-refundable deposit is required to book. If a deposit has not been paid, I will take this as you don't want the appointment."
    },
    {
      title: "Late Arrival",
      description: "If you are more than 15 minutes late there may be a late charge at the end of your session."
    },
    {
      title: "Cancellation Policy",
      description: "If you need to cancel your appointment please let me know 72 hours before your slot, otherwise a cancellation fee is required."
    },
    {
      title: "Booking Method",
      description: "Please note: all bookings are taken through DM or the booking form on this website."
    }
  ];

  return (
    <section
      id="policies"
      className="py-32 bg-dark relative"
      data-testid="policies-section"
    >
      <div className="max-w-[1000px] mx-auto px-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <p className="text-xs uppercase tracking-[0.3em] text-accent-purple mb-4">Important Information</p>
          <h2 className="section-title mb-4">Policies</h2>
          <p className="font-accent text-2xl text-slate-400 italic">& reminders</p>
        </motion.div>

        <div className="glass rounded-2xl p-8 md:p-12">
          <h3 className="text-xs uppercase tracking-[0.3em] text-slate-500 mb-8 text-center border-b border-white/10 pb-4">
            Booking Policies
          </h3>

          <div className="space-y-8">
            {policies.map((policy, index) => (
              <motion.div
                key={policy.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="flex gap-6 items-start border-b border-white/5 pb-8 last:border-0 last:pb-0"
                data-testid={`policy-${index}`}
              >
                <span className="text-accent-purple text-2xl font-light">—</span>
                <div>
                  <h4 className="font-medium text-lg mb-2">{policy.title}</h4>
                  <p className="text-slate-400 leading-relaxed">{policy.description}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

// Footer
const Footer = () => {
  return (
    <footer className="py-16 bg-dark border-t border-white/5" data-testid="footer">
      <div className="max-w-[1400px] mx-auto px-6">
        <div className="flex flex-col md:flex-row items-center justify-between gap-8">
          <div className="text-center md:text-left">
            <h3 className="font-heading text-3xl mb-2">TATTOOS BY HET</h3>
            <p className="text-slate-500 text-sm">Luxury Custom Tattoo Studio</p>
          </div>

          <div className="flex gap-6">
            <a
              href="https://www.instagram.com/tattoosbyhet?igsh=ZjhvYTIwdXg3N3I4"
              target="_blank"
              rel="noopener noreferrer"
              className="w-12 h-12 rounded-full border border-white/10 flex items-center justify-center hover:border-accent-purple hover:text-accent-purple transition-colors"
              data-testid="footer-instagram"
            >
              <Instagram size={20} />
            </a>
            <a
              href="mailto:tattoosbyhet07@gmail.com"
              className="w-12 h-12 rounded-full border border-white/10 flex items-center justify-center hover:border-accent-purple hover:text-accent-purple transition-colors"
              data-testid="footer-email"
            >
              <Mail size={20} />
            </a>
          </div>
        </div>

        <div className="mt-12 pt-8 border-t border-white/5 text-center">
          <p className="text-slate-600 text-sm">
            © {new Date().getFullYear()} Tattoos by Het. All rights reserved.
          </p>
        </div>
      </div>
    </footer>
  );
};

// Main Home Component
export default function Home() {
  const [portfolio, setPortfolio] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [paymentStatus, setPaymentStatus] = useState(null);

  useEffect(() => {
    // Check for payment status in URL
    const urlParams = new URLSearchParams(window.location.search);
    const payment = urlParams.get('payment');
    const bookingId = urlParams.get('booking_id');

    if (payment === 'success' && bookingId) {
      // Verify payment with backend (which checks Stripe directly)
      verifyPayment(bookingId)
        .then((res) => {
          if (res.data.status === 'paid') {
            setPaymentStatus('success');
            toast.success('Deposit paid successfully! Your booking is being processed.');
          } else {
            toast.error('Payment could not be verified. Please contact us.');
          }
        })
        .catch(() => {
          // Even if verify fails, show success since Stripe redirected them
          setPaymentStatus('success');
          toast.success('Deposit paid! Your booking is being processed.');
        });
      // Remove params from URL without refreshing
      window.history.replaceState({}, document.title, window.location.pathname);
    } else if (payment === 'cancelled') {
      toast.error('Payment was cancelled. Your booking was not completed.');
      window.history.replaceState({}, document.title, window.location.pathname);
    }

    const loadData = async () => {
      try {
        // Load all data
        const [portfolioRes, testimonialsRes, settingsRes] = await Promise.all([
          getPortfolio(),
          getTestimonials(),
          getSettings()
        ]);

        setPortfolio(portfolioRes.data);
        setTestimonials(testimonialsRes.data);
        setSettings(settingsRes.data);
      } catch (error) {
        console.error('Error loading data:', error);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, []);

  if (loading) {
    return (
      <div className="h-screen bg-dark flex items-center justify-center">
        <motion.div
          animate={{ rotate: 360 }}
          transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
          className="w-8 h-8 border-2 border-accent-purple border-t-transparent rounded-full"
        />
      </div>
    );
  }

  return (
    <main className="bg-dark">
      <Navigation />
      <HeroSection />
      <AboutSection />
      <StylesSection portfolio={portfolio} />
      <GallerySection portfolio={portfolio} />
      <StudioSection />
      <TestimonialsSection testimonials={testimonials} />
      <PoliciesSection />
      <BookingSection settings={settings} forceSuccess={paymentStatus === 'success'} />
      <Footer />
    </main>
  );
}
